import Subscriptions from "../Subscriptions";
import {buildURLsFromHost, mapServiceURLs, decodeJWT} from "./utils";

export const windowKey = "atsSecurityRibbonServices";

export const credentialsSet = "ats-services-auth-token-set";

export const hostSet = "ats-services-host-set";

export const hostConfigFetched = "ats-services-host-config-fetched";

export const apiHostSet = "ats-services-api-host-set";

export const subscriptionKeys = {
  credentialsSet,
  apiHostSet,
  hostSet,
  servicesError: "ats-services-error",
  hostConfigFetched
};

const validateString = (key, value) => {
  if (
    value !== "" &&
    value !== undefined &&
    value !== null &&
    typeof value !== "string"
  ) {
    throw new Error(`Value for <${key}> must be of type 'string'.`);
  }
};

const validateObject = (key, value) => {
  if (value && typeof value !== "object") {
    throw new Error(`Value for <${key}> must be of type 'object'.`);
  }
};

const validateFunc = (key, value) => {
  if (value && typeof value !== "function") {
    throw new Error(`Value for <${key}> must be of type 'function'.`);
  }
};

const requiredIntegrationKeys = ["name"];

export class Services {
  constructor() {
    this.subscriptionKeys = subscriptionKeys;

    this._ribbonConfiguration = {};
  }

  get credentialsKey() {
    return this._tokenKey || ":iroh-auth-token";
  }

  set credentialsKey(value) {
    validateString("credentialsKey", value);

    this._tokenKey = value;
  }

  get isDefaultAccount() {
    return this._isDefaultAccount;
  }

  set isDefaultAccount(value) {
    this._isDefaultAccount = value;
  }

  get host() {
    return this._host;
  }

  set host(value) {
    validateString("host", value);

    if (value && value.endsWith("/")) {
      value = value.slice(0, -1);
    }

    if (value === this._host) {
      return;
    }

    const previousValue = this._host;

    this._host = value;

    if (previousValue !== value) {
      Subscriptions.publish(subscriptionKeys.hostSet, value, previousValue);
    }
  }

  get apiHost() {
    return this._apiHost;
  }

  set apiHost(value) {
    validateString("apiHost", value);

    if (value.endsWith("/")) {
      value = value.slice(0, -1);
    }

    if (value === this._apiHost) {
      return;
    }

    const previousValue = this._apiHost;

    this._apiHost = value;

    if (previousValue !== value) {
      Subscriptions.publish(subscriptionKeys.apiHostSet, value, previousValue);
    }
  }

  get uiHost() {
    return this._uiHost;
  }

  set uiHost(value) {
    validateString("uiHost", value);

    this._uiHost = value;
  }

  get privateURL() {
    return this._privateURL;
  }

  set privateURL(value) {
    validateString("privateURL", value);

    if (value.endsWith("/")) {
      value = value.slice(0, -1);
    }

    this._privateURL = value;
  }

  get publicURL() {
    return this._publicURL;
  }

  set publicURL(value) {
    validateString("publicURL", value);

    if (value.endsWith("/")) {
      value = value.slice(0, -1);
    }

    this._publicURL = value;
  }

  get orbitalURL() {
    return this._orbitalURL;
  }

  set orbitalURL(value) {
    validateString("orbitalURL", value);

    if (value.endsWith("/")) {
      value = value.slice(0, -1);
    }

    this._orbitalURL = value;
  }

  get sdkUrl() {
    return this._sdkUrl;
  }

  set sdkUrl(value) {
    this._sdkUrl = value;

    this.cdnDomain = value;
  }

  get cdnDomain() {
    return this._cdnDomain;
  }

  set cdnDomain(value) {
    if (!value) {
      return;
    }

    try {
      const url = new URL(value),
        {hostname} = url,
        baseDomainSplit = hostname.split(".");

      // Take off the first subdomain
      if (baseDomainSplit.length === 4) {
        baseDomainSplit.shift();
      }

      const baseDomain = baseDomainSplit.join(".");

      this._cdnDomain = baseDomain;
    } catch (e) {
      console.error(e);
    }
  }

  get authorizeFunction() {
    return this._authorizeFunction;
  }

  set authorizeFunction(value) {
    this._authorizeFunction = value;
  }

  get authorizeLink() {
    return this._authorizeLink;
  }

  set authorizeLink(value) {
    validateString("authorizeLink", value);

    this._authorizeLink = value;
  }

  get revokeLink() {
    return this._revokeLink;
  }

  set revokeLink(value) {
    validateString("revokeLink", value);

    this._revokeLink = value;
  }

  get revokeAction() {
    return this._revokeAction;
  }

  set revokeAction(value) {
    validateFunc(this.revokeAction, value);

    this._revokeAction = value;
  }

  get integration() {
    return this._integration;
  }

  set integration(integration) {
    validateObject("integration", integration);
    const keys = Object.keys(integration);

    keys.forEach(key => {
      validateString(`${this.integration}-${key}`, integration[key]);
    });

    requiredIntegrationKeys.forEach(k => {
      if (!keys.includes(k)) {
        console.warn(`integration must contain key '${k}'.`);
      }
    });

    this._integration = integration;
  }

  get credentials() {
    let storedCredentials;

    // First check in session storage
    if (window.sessionStorage) {
      storedCredentials = window.sessionStorage.getItem(this.credentialsKey);
    }

    // If not there, check local storage
    if (!storedCredentials && window.localStorage) {
      storedCredentials = window.localStorage.getItem(this.credentialsKey);
    }

    // If the token has changed, update our class var
    if (storedCredentials && this._credentials !== storedCredentials) {
      this.credentials = storedCredentials;
    }

    return this._credentials;
  }

  set credentials(credentials) {
    validateString("credentials", credentials);

    if (credentials) {
      credentials = credentials.replace("Bearer ", "");
    }

    if (this._credentials === credentials) {
      return;
    }

    this._credentials = credentials;

    Subscriptions.publish(subscriptionKeys.credentialsSet, this.credentials);
  }

  get clientId() {
    return this._clientId;
  }

  set clientId(value) {
    validateString("clientId", value);

    this._clientId = value;
  }

  get clientSecret() {
    return this._clientSecret;
  }

  set clientSecret(value) {
    validateString("clientSecret", value);

    this._clientSecret = value;
  }

  get claimScopes() {
    if (!this.credentials) {
      return [];
    }

    const jwt = decodeJWT(this.credentials);

    if (!jwt) {
      return [];
    }

    return jwt["https://schemas.cisco.com/iroh/identity/claims/scopes"];
  }

  get jwt() {
    const jwt = decodeJWT(this.credentials);

    if (!jwt) {
      return [];
    }

    return jwt;
  }

  get isExtension() {
    return this._isExtension;
  }

  set isExtension(value) {
    this._isExtension = value;
  }

  get isMobile() {
    return this._isMobile;
  }

  set isMobile(value) {
    this._isMobile = value;
  }

  get isInjected() {
    return this._isInjected;
  }

  set isInjected(value) {
    this._isInjected = value;
  }

  get ribbonConfiguration() {
    return this._ribbonConfiguration;
  }

  set ribbonConfiguration(value) {
    this._ribbonConfiguration = value;
  }

  get orgEntitled() {
    return this._orgActivated;
  }

  set orgEntitled(value) {
    if (this._orgActivated === value) {
      return;
    }

    this._orgActivated = value;

    Subscriptions.publish(subscriptionKeys.orgActivatedSet, value);
  }

  get ribbonVersion() {
    return this._ribbonVersion;
  }

  set ribbonVersion(value) {
    this._ribbonVersion = value;
  }

  get hasRetryInterceptor() {
    return this._hasRetryInterceptor;
  }

  set hasRetryInterceptor(value) {
    this._hasRetryInterceptor = value;
  }

  // Can override the call mechanism, for example swapping
  // XMLHttpRequest for fetch. Used by the browser extension.
  get axiosAdapter() {
    return this._axiosAdapter;
  }

  set axiosAdapter(value) {
    this._axiosAdapter = value;
  }

  setServiceURLs(hostConfig, force = false) {
    const mapped = hostConfig
      ? mapServiceURLs(hostConfig, force)
      : buildURLsFromHost(this.host);

    Object.keys(mapped).forEach(k => {
      if (mapped[k] && (!this[k] || force)) {
        if (this[k] !== mapped[k] && !this.ribbonConfiguration[k]) {
          this[k] = mapped[k];
        }
      }
    });
  }

  clear() {
    Object.keys(this)
      .filter(k => k.startsWith("_"))
      .forEach(k => {
        // Once extension, always extension.
        if (
          [
            "_isExtension",
            "_isInjected",
            "_integration",
            "_ribbonVersion"
          ].includes(k)
        ) {
          return;
        }

        this[k.replace("_", "")] = "";
      });
  }
}

const Configuration = () => {
  if (!window[windowKey]) {
    window[windowKey] = new Services();
  }
  return window[windowKey];
};

export default Configuration();
